'use client'

import { useState, useEffect } from 'react'
import { TFunction } from 'i18next'
import { Trans, useTranslation } from 'react-i18next'
import Link from 'next/link'

import { Lang } from '@/i18n'
import type { LandingCounters } from '@/services/counters.service'
import { Country } from '@/types'

import CountUp from '@/components/count-up'
import CountryFlag from '@/components/country-flag/country-flag'
import { Spinner3 } from '@/components/icons/spinner'

import Searchbar from '../searchbar'
import { destinationToSlug } from '../offers/helpers'

type LangKey = 'en' | 'bs' | 'hr' | 'sr'

// `short` is the chip label; the full name is still used for the offers URL.
const popularDestinations: Array<Country & { short?: Partial<Record<LangKey, string>> }> = [
  { code: 'Europe', name: '', en: 'Europe', bs: 'Europa', hr: 'Europa', sr: 'Evropa' },
  { code: 'tr', name: '', en: 'Turkiye', bs: 'Turska', hr: 'Turska', sr: 'Turska' },
  { code: 'hr', name: '', en: 'Croatia', bs: 'Hrvatska', hr: 'Hrvatska', sr: 'Hrvatska' },
  { code: 'it', name: '', en: 'Italy', bs: 'Italija', hr: 'Italija', sr: 'Italija' },
  {
    code: 'sa',
    name: '',
    en: 'Saudi Arabia',
    bs: 'Saudijska Arabija',
    hr: 'Saudijska Arabija',
    sr: 'Saudijska Arabija',
    short: { en: 'Saudi Arabia', bs: 'S. Arabija', hr: 'S. Arabija', sr: 'S. Arabija' },
  },
  { code: 'es', name: '', en: 'Spain', bs: 'Španija', hr: 'Španija', sr: 'Španija' },
]

function getUserProfile() {
  try {
    return JSON.parse(localStorage.getItem('profile') || '{}')
  } catch {
    return null
  }
}

const COUNTERS_POLL_MS = 30_000

const formatTimeAgo = (purchasedAt: string, now: number, t: TFunction) => {
  const minutes = Math.floor((now - new Date(purchasedAt).getTime()) / 60_000)
  if (minutes < 1) return t('Landing.Text.LastPurchaseJustNow')
  if (minutes < 60) return t('Landing.Text.LastPurchaseMinutes', { value: minutes })
  if (minutes < 60 * 24) return t('Landing.Text.LastPurchaseHours', { value: Math.floor(minutes / 60) })
  return t('Landing.Text.LastPurchaseDays', { value: Math.floor(minutes / (60 * 24)) })
}

const Hero = ({ lang, counters: initialCounters }: { lang: Lang; counters: LandingCounters | null }) => {
  const [profile, setProfile] = useState<undefined | { name: string }>(undefined)
  const [counters, setCounters] = useState(initialCounters)
  const [now, setNow] = useState<number | null>(null)
  const [pendingCode, setPendingCode] = useState<string | null>(null)
  const { t, i18n } = useTranslation()
  const langKey = (i18n.language as LangKey) || 'bs'

  useEffect(() => {
    const storedProfile = getUserProfile()
    if (storedProfile) {
      setProfile(storedProfile)
    }
  }, [])

  useEffect(() => {
    let ignore = false

    setNow(Date.now())

    const refresh = async () => {
      if (document.visibilityState === 'hidden') {
        return
      }

      setNow(Date.now())

      try {
        const response = await fetch('/api/counters', { cache: 'no-store' })
        if (!response.ok) {
          return
        }
        const data = (await response.json()) as LandingCounters | null
        if (ignore || !data) {
          return
        }
        setCounters(data)
      } catch {
        // Keep the last known totals when a refresh fails.
      }
    }

    const interval = window.setInterval(refresh, COUNTERS_POLL_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        refresh()
      }
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      ignore = true
      window.clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  const lastPurchase = counters?.lastPurchase
  const lastPurchaseTime = lastPurchase && now ? formatTimeAgo(lastPurchase.purchasedAt, now, t) : null

  const i18nKey = profile?.name ? 'Landing.Heading.WhereAreYouTraveling' : 'Landing.Heading.WhereAreYouTravelingGuest'

  return (
    <section id='hero' data-track-section='hero' className='bg-white-50 relative scroll-mt-28 py-8 px-4 sm:px-6 lg:px-8'>
      <div className='max-w-5xl mx-auto text-center'>
        <h1 className='text-[28px] leading-tight sm:text-4xl md:text-5xl lg:text-5xl xl:text-7xl font-bold text-blue-950 mb-4 md:mb-6'>
          <Trans
            i18nKey={i18nKey}
            values={{ name: profile?.name ? profile?.name?.split(' ')?.[0] : '' }}
            components={[<span className='block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600' key='1' />]}
          />
        </h1>
        <p className='text-lg text-gray-600 mb-8 max-w-2xl mx-auto'>
          <span className='min-[720px]:hidden'>
            <Trans i18nKey='Landing.Text.Description' components={[<br key='1' />]} />
          </span>
          <span className='hidden min-[720px]:inline'>
            <Trans i18nKey={lang === 'bs' ? 'Landing.Text.DescriptionDesktop' : 'Landing.Text.Description'} components={[<br key='1' />]} />
          </span>
        </p>
      </div>
      {pendingCode && (
        <div className='fixed inset-0 z-20 flex items-center justify-center bg-white/70'>
          <Spinner3 className='h-10 w-10' />
        </div>
      )}
      <div className='max-w-2xl mx-auto mb-8'>
        <Searchbar lang={lang} loadOnOpen />
        <div className='mt-4 grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:justify-center'>
          {popularDestinations.map(item => {
            const name = (item[langKey] as string) || (item.en as string)
            const label = item.short?.[langKey] || name
            return (
              <Link
                key={item.code}
                href={`/${i18n.language}/offers/${destinationToSlug(name)}`}
                prefetch={false}
                onClick={() => setPendingCode(item.code)}
                className='inline-flex h-9 min-w-0 items-center justify-center gap-1.5 rounded-full border border-slate-200 bg-white px-2 text-[13px] font-medium text-blue-950 shadow-sm transition-colors hover:border-blue-200 hover:bg-blue-50 sm:gap-2 sm:px-3 sm:text-sm'
              >
                <span className='flex size-5 shrink-0 items-center justify-center overflow-hidden rounded-full [&_svg]:size-5'>
                  <CountryFlag country={item} className='!size-5 ![background-size:cover]' />
                </span>
                <span className='truncate'>{label}</span>
              </Link>
            )
          })}
        </div>
      </div>
      {counters && (
        <div className='flex flex-col items-center'>
          <div className='flex items-center justify-center gap-8 sm:gap-16'>
            <CountUp value={counters.totalOrders} label={t('Landing.Text.Purchases')} duration={1800} showIncrement />
            <div className='h-16 w-px shrink-0 bg-gray-300' aria-hidden='true' />
            <CountUp value={counters.totalUsers} label={t('Landing.Text.Users')} duration={1800} />
          </div>
          <p className='mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-[13px] font-medium text-emerald-800 ring-1 ring-inset ring-emerald-600/15'>
            <span className='relative flex size-2 shrink-0' aria-hidden='true'>
              <span className='absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-75 motion-reduce:hidden' />
              <span className='relative inline-flex size-2 rounded-full bg-emerald-500' />
            </span>
            {lastPurchase ? (
              <span>
                <Trans
                  i18nKey='Landing.Text.LastPurchase'
                  values={{ destination: lastPurchase.destination[langKey] || lastPurchase.destination.en, volume: lastPurchase.volume ?? t('Landing.Text.Unlimited') }}
                  tOptions={{ interpolation: { escapeValue: false } }}
                  components={[<strong className='font-bold' key='0' />]}
                />
                {lastPurchaseTime && ` · ${lastPurchaseTime}`}
              </span>
            ) : (
              t('Landing.Text.LiveCounters')
            )}
          </p>
        </div>
      )}
    </section>
  )
}

export default Hero
